import styles from "../../../shared/components/shell.module.css";
import { SUPPORT_EMAIL } from "../lib/support.js";

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * License state of the school.
 *
 * Says out loud what happens after it expires: the children cannot sign in, and
 * 60 days later everything is deleted. A head teacher must not learn that from
 * an empty screen.
 *
 * Read-only, and that is a decision, not an omission: licence, name and contact
 * are maintained by the platform. A school admin who could set `license_until`
 * could move the payment boundary of the business model — that used to be
 * possible and is now revoked in the database as well.
 *
 * So every state here says where to turn. Without that sentence the screen
 * states a problem and offers no way out, and the first thing a head teacher
 * does is look for the button that is deliberately absent.
 */
export function LicenseCard({
  schoolName,
  licenseUntil,
}: {
  schoolName: string;
  licenseUntil: string | null;
}) {
  const daysLeft = licenseUntil
    ? Math.ceil((new Date(licenseUntil).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>{schoolName}</h1>
      {licenseUntil ? (
        <p className={styles.subtitle}>
          Lizenz läuft bis <strong>{dateFormat.format(new Date(licenseUntil))}</strong>
          {daysLeft !== null && daysLeft >= 0 ? ` (${daysLeft} Tage)` : ""}.
          {daysLeft !== null && daysLeft < 0 && (
            <>
              {" "}
              <span style={{ color: "var(--warning)" }}>
                Die Lizenz ist abgelaufen – die Kinder können sich nicht mehr anmelden. 60
                Tage nach Ablauf werden alle Übungscodes und Ergebnisse automatisch
                gelöscht.
              </span>{" "}
              Für eine Verlängerung schreiben Sie an{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </>
          )}
        </p>
      ) : (
        <p className={styles.subtitle}>
          Für diese Schule ist keine Lizenz eingetragen – die Kinder können sich noch
          nicht anmelden. Schreiben Sie an{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, dann tragen wir sie
          ein.
        </p>
      )}

      <p className={styles.subtitle} style={{ marginBottom: 0 }}>
        Name der Schule, Ansprechpartner und Lizenz pflegen wir für Sie – deshalb gibt es
        hier kein Eingabefeld dafür. Für Änderungen genügt eine E-Mail an{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Lehrkräfte und Klassen
        legen Sie selbst an.
      </p>
    </div>
  );
}
